import { scaleLinear } from 'd3-scale';
import { forceSimulation, Simulation, forceX, forceY, forceCollide } from 'd3-force';
import GlobalModel from 'echarts/types/src/model/Global';
import ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import { BeesWarmGraphSeriesModel, NodeFormatted } from '.';
import { AxisBaseModel } from 'echarts/types/src/coord/AxisBaseModel';
import merge from 'deepmerge';

// 力导向默认参数
const DEFAULT_ARGUMENTS = {
  // alpha值
  alpha: 0.5,
  // alpha最小值
  alphaMin: 0.1,
  // alpha目标值
  alphaTarget: 0,
  // alpha衰减率，默认值0.0228 = 1 - pow(0.001 ,1 / 300)
  alphaDecay: 0.004,
  // 速度衰减率，默认值0.4
  velocityDecay: 0.6,
  // X方向力
  xStrength: 0.01,
  // Y方向力
  yStrength: 0.1
};

// 扩展配置项
interface BeeswarmOption {
  axisIndex?: number;
  extremeHandle?: {
    // 映射区域
    mapRange: [number, number];
    // 分段节点
    normalExtent: [number, number];
    // 分段节点
    borderValue: [number, number, number, number];
  };
  animation: boolean;
}

export interface InputNode {
  point: number[];
  fixed: boolean;
  value: number;
  radius: number;
}

export interface D3Node {
  x: number;
  y: number;
  fx?: number;
  fy?: number;
  tx: number;
  ty: number;
  r: number;
}

export function beeswarmLayout(
  api: ExtensionAPI,
  globalModel: GlobalModel,
  inNodes: InputNode[],
  graphSeries: BeesWarmGraphSeriesModel
) {
  let isEnd = false;

  const nodes = inNodes;

  // 获取coord 世界坐标
  const coord = graphSeries.coordinateSystem;
  const rect = coord.getBoundingRect!();

  const { width } = rect;
  const { height } = rect;
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const beeswarmModel = graphSeries.getModel('beeswarm') || {};
  const animation = beeswarmModel.get('animation') === false ? false : true;
  const axisIndex: number = beeswarmModel?.get('axisIndex') || 0;
  const args = beeswarmModel.get('args') || {};
  const argsMerged = merge(DEFAULT_ARGUMENTS, args);
  const axisIns = globalModel.getComponent('yAxis', axisIndex) as AxisBaseModel;

  if (!axisIns) {
    throw new Error('beeswarm layout need axis');
  }
  const axis2D = axisIns.axis;
  const extremeHandle = beeswarmModel.get('extremeHandle') as BeeswarmOption['extremeHandle'];

  let targetsY: number[] = [];

  function data2Coord(value: number) {
    return (axis2D as any).toGlobalCoord(axis2D.dataToCoord(value));
  }

  if (!extremeHandle) {
    targetsY = nodes.map(d => data2Coord(d.value));
  } else {
    const sortedNodes = merge([], nodes).sort((a, b) => a.value - b.value);
    const values = sortedNodes.map(d => d.value);
    const { mapRange, borderValue } = extremeHandle;
    const gap = 1;
    // [0, normalExtent[0]), [normalExtent[0], normalExtent[1]], [normalExtent[1], length)

    const axisMin = axisIns.option.min;
    const axisMax = axisIns.option.max;

    if (!axisMin || !axisMax) {
      throw new Error('extremeHandle of beeswarm layout need axis min and max');
    }

    const extremeMapMin = mapRange[0];
    const extremeMapMax = mapRange[1];
    // 生成extremeMin、normal、extremeMax三部分的比例尺
    const extremeMinDomain = borderValue.slice(0, 2);
    const extremeMinRange = [
      data2Coord(axisMin as number),
      // 防止小球超出边界
      data2Coord(extremeMapMin) - sortedNodes[0].radius - gap
    ];
    const normalDomain = borderValue.slice(1, 3);
    const normalRange = [data2Coord(extremeMapMin), data2Coord(extremeMapMax)];
    const extremeMaxDomain = borderValue.slice(2, 4);
    const extremeMaxRange = [
      data2Coord(extremeMapMax),
      // 防止小球超出边界
      data2Coord(axisMax as number) + sortedNodes[sortedNodes.length - 1].radius + gap
    ];
    const extremeMinScale = scaleLinear().domain(extremeMinDomain).range(extremeMinRange);

    const normalScale = scaleLinear().domain(normalDomain).range(normalRange);

    const extremeMaxScale = scaleLinear().domain(extremeMaxDomain).range(extremeMaxRange);

    targetsY = nodes.map(d => {
      const { value } = d;
      if (value < normalDomain[0]) {
        return extremeMinScale(value);
      }
      if (value > normalDomain[1]) {
        return extremeMaxScale(value);
      }
      return normalScale(value);
    });
  }

  // Init d3 nodes

  const d3Nodes: D3Node[] = nodes.map((d, i) => {
    const targetX = rect.x + halfWidth;
    const targetY = targetsY[i];
    if (!d.point) {
      // eslint-disable-next-line no-magic-numbers
      d.point = [targetX + halfWidth * (Math.random() - 0.5), targetY];
    }

    const result: D3Node = {
      x: d.point[0],
      y: d.point[1],
      tx: targetX,
      ty: targetY,
      r: d.radius
    };

    if (d.fixed) {
      result.fx = d.point[0];
      result.fy = d.point[1];
    }

    return result;
  });

  const minR = Math.min(...d3Nodes.map(d => d.r)) || 0;
  const gap = minR / 10;

  let innerTickCallback: (nodes: D3Node[]) => void;
  let tickCallback: (nodes: D3Node[]) => void;
  let simulation: Simulation<D3Node, undefined>;
  let debounceTimer: NodeJS.Timeout;
  return {
    getD3Nodes: () => d3Nodes,
    getSimulation: () => simulation,
    isEnd: () => isEnd,
    start: () => {
      simulation.restart();
    },
    /** initialization */
    init: () => {
      const tickFn = () => {
        innerTickCallback && innerTickCallback(d3Nodes);
        tickCallback && tickCallback(d3Nodes);
      };
      simulation = forceSimulation(d3Nodes)
        .alpha(argsMerged.alpha)
        .alphaMin(argsMerged.alphaMin)
        .alphaTarget(argsMerged.alphaTarget)
        .alphaDecay(argsMerged.alphaDecay)
        .velocityDecay(argsMerged.velocityDecay)
        .force(
          'x',
          forceX()
            .x(d => (d as D3Node).tx)
            .strength(argsMerged.xStrength)
        )
        .force(
          'y',
          forceY()
            .y(d => (d as D3Node).ty)
            .strength(argsMerged.yStrength)
        )
        .force(
          'collide',
          forceCollide().radius(d => (d as D3Node).r + gap)
        )
        // 停止模拟以便手动控制
        .stop()
        .on('end', () => {
          isEnd = true;
        })
        .on('tick', () => {
          if (animation) {
            tickFn();
          } else {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
              tickFn();
            }, 200);
          }
        });
    },
    /**
     * inner tick hook
     */
    innerCallBack: function (cb: typeof innerTickCallback) {
      innerTickCallback = cb;
      // 立即执行初始化setLayout
      cb(d3Nodes);
    },
    /**
     * out tick hook
     */
    callback: function (cb: typeof tickCallback) {
      tickCallback = cb;
    },
    dispose: () => {
      simulation.stop();
    }
  };
}
